import { toProblem } from '@lib/api/base';
import type { UnreadCommentSummary } from '@lib/api/types';
import { formatDistanceToNow } from 'date-fns';
import { Bell, Inbox, Loader2, MessageCircle } from 'lucide-react';
import React from 'react';
import { Link } from 'react-router-dom';

import { useNotificationsInbox } from '@/hooks/useOfferComments';

import { Alert, AlertDescription } from '@ui/alert';
import { ScrollArea } from '@ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@ui/sheet';
import { SidebarMenuButton } from '@ui/sidebar';

/**
 * NotificationsInbox Component
 *
 * A Notion-style inbox that shows unread comments from candidates.
 * Displayed as a slide-out panel from the sidebar.
 */
export const NotificationsInbox: React.FC = () => {
  const { data: inbox, isLoading, error } = useNotificationsInbox();

  return (
    <Sheet>
      <SheetTrigger asChild>
        <SidebarMenuButton className="w-full relative" data-testid="notifications-inbox-trigger">
          <Inbox className="h-4 w-4" />
          <span>Inbox</span>
          {inbox && inbox.total_unread > 0 && (
            <span className="absolute right-2 top-1/2 -translate-y-1/2 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-xs font-medium text-primary-foreground">
              {inbox.total_unread > 99 ? '99+' : inbox.total_unread}
            </span>
          )}
        </SidebarMenuButton>
      </SheetTrigger>
      <SheetContent side="left" className="w-[400px] sm:w-[450px] p-0">
        <SheetHeader className="p-4 border-b">
          <SheetTitle className="flex items-center gap-2">
            <Inbox className="h-5 w-5 text-primary" />
            Inbox
            {inbox && inbox.total_unread > 0 && (
              <span className="text-sm font-normal text-muted-foreground">
                ({inbox.total_unread} unread)
              </span>
            )}
          </SheetTitle>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-80px)]">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <span className="ml-2 text-sm text-muted-foreground">Loading notifications...</span>
            </div>
          ) : error ? (
            <div className="p-4" data-testid="notifications-error-alert">
              <Alert variant="destructive">
                <AlertDescription>{toProblem(error).detail}</AlertDescription>
              </Alert>
            </div>
          ) : !inbox || inbox.items.length === 0 ? (
            <EmptyInbox />
          ) : (
            <div className="divide-y divide-border">
              {inbox.items.map(item => (
                <NotificationItem key={item.offer_id} item={item} />
              ))}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

/**
 * Empty inbox state
 */
const EmptyInbox: React.FC = () => (
  <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
    <div className="p-4 bg-muted rounded-full mb-4">
      <Bell className="h-8 w-8 text-muted-foreground" />
    </div>
    <h3 className="text-lg font-medium text-foreground mb-1">No messages yet</h3>
    <p className="text-sm text-muted-foreground">
      Candidate messages on your offers will appear here.
    </p>
  </div>
);

/**
 * Individual notification item
 */
interface NotificationItemProps {
  item: UnreadCommentSummary;
}

const NotificationItem: React.FC<NotificationItemProps> = ({ item }) => {
  const timeAgo = item.latest_comment_at
    ? formatDistanceToNow(new Date(item.latest_comment_at), { addSuffix: true })
    : '';

  const hasUnread = item.unread_count > 0;

  return (
    <Link
      to={`/offers/${item.offer_id}/`}
      className={`block p-4 hover:bg-muted transition-colors ${hasUnread ? 'bg-primary/5' : ''}`}
      data-testid={`notification-${item.offer_id}`}
    >
      <div className="flex items-start gap-3">
        {/* Avatar/Icon */}
        <div className="flex-shrink-0 relative">
          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
            <span className="text-sm font-medium text-primary">
              {item.candidate_name.charAt(0).toUpperCase()}
            </span>
          </div>
          {/* Unread indicator dot */}
          {hasUnread && (
            <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-primary border-2 border-card" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p
                className={`text-sm truncate ${hasUnread ? 'font-semibold text-foreground' : 'font-medium text-muted-foreground'}`}
              >
                {item.candidate_name}
              </p>
              <p className="text-xs text-muted-foreground truncate">{item.role}</p>
            </div>
            <div className="flex-shrink-0 flex items-center gap-2">
              {item.has_negotiation && (
                <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700">
                  Negotiation
                </span>
              )}
              {hasUnread ? (
                <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-xs font-medium text-primary-foreground">
                  {item.unread_count}
                </span>
              ) : (
                <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-muted px-1.5 text-xs font-medium text-muted-foreground">
                  {item.total_count}
                </span>
              )}
            </div>
          </div>

          {/* Preview */}
          {item.latest_comment && (
            <div className="mt-1 flex items-start gap-1">
              <MessageCircle className="h-3 w-3 text-muted-foreground mt-0.5 flex-shrink-0" />
              <p
                className={`text-sm line-clamp-2 ${hasUnread ? 'text-foreground' : 'text-muted-foreground'}`}
              >
                {item.latest_comment}
              </p>
            </div>
          )}

          {/* Time */}
          {timeAgo && <p className="text-xs text-muted-foreground mt-1">{timeAgo}</p>}
        </div>
      </div>
    </Link>
  );
};
